/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { BEBOP_ROUTES } from "config/consts";
import { partition, some } from "lodash-es";
import { useStoreState } from "pullstate";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { RegionRestrictedModal } from "../../components/Modal/instance/RegionRestrictedModal/RegionRestrictedModal";
import { WrappingQuoteModal } from "../../components/Modal/instance/WrappingQuoteModal/WrappingQuoteModal";
import { MODAL_SELECTORS } from "../../components/Modal/instance/modalSelectors";
import { Spinner } from "../../components/Spinner/Spinner";
import { getQuoteWarnings } from "../../config/errorCodes";
import { CommonButtonState } from "../../enums/CommonButtonState";
import { ReactComponent as WalletIcon } from "../../img/wallet.svg";
import { GlobalStore, IGlobalStore } from "../../store/global/GlobalStore";
import { Allowances } from "../../store/global/getAllowances";
import { OrderStatus } from "../../types/orderStatus";
import { WrapStatus } from "../../types/wrapQuote";
import { getTokensToApprove, shouldRevokeFirst } from "../../utils/allowanceUtils";
import { isUserUSBased } from "../../utils/regionRestrictUtils";
import { isReceiverValid } from "../../utils/swapAndSendUtils";
import { ApprovalStore } from "../../store/approvals/ApprovalsStore";

export const InitialActionButtonState = {
  ...CommonButtonState,
  InsufficientAllowance: "InsufficientAllowance",
  Wrapping: "Wrapping",
  Unwrapping: "Unwrapping",
  WrongNetwork: "WrongNetwork",
  SelectTokens: "SelectTokens",
  ReviewOrder: "ReviewOrder",
  IgnoreAndReview: "IgnoreAndReview",
  RequestingQuote: "RequestingQuote",
  WrappingQuote: "WrappingQuote", // When user wants to go Native token -> ERC20
  EnterAmounts: "EnterAmounts",
  Error: "Error",
  IncreaseOrderSize: "IncreaseOrderSize",
  SwapAndSendError: "SwapAndSendError",
  EnterAddress: "EnterAddress",
  AddressLoading: "AddressLoading",
  NoPricing: "NoPricing",
};

export type ActionButtonStateKeys = keyof typeof InitialActionButtonState;
const validateOrderEntry = (store: IGlobalStore): ActionButtonStateKeys => {
  const tokensToApprove: Allowances = getTokensToApprove(store.quote, store.allowances);

  if (!store.address) {
    return InitialActionButtonState.WalletNotConnected as ActionButtonStateKeys;
  }
  if (store.makerTokens.length === 0 || store.takerTokens.length === 0) {
    return InitialActionButtonState.SelectTokens as ActionButtonStateKeys;
  }

  if (some(store.takerTokens, entry => entry.amount.value.eq(0))) {
    return InitialActionButtonState.EnterAmounts as ActionButtonStateKeys;
  }

  // If wrapQuote is not null or undefined and that wrapQuote.amount is undefined
  if (store.wrapQuote?.amount !== undefined) {
    if (store.wrapQuote.type === "Wrapping") {
      return InitialActionButtonState.Wrapping as ActionButtonStateKeys;
    } else if (store.wrapQuote.type === "Unwrapping") {
      return InitialActionButtonState.Unwrapping as ActionButtonStateKeys;
    }
  }

  if (isQuantityLargerThanBalance(store)) {
    return InitialActionButtonState.InsufficientBalance as ActionButtonStateKeys;
  }

  if (store.quoteError !== undefined) {
    if (store.quoteError?.error.errorCode === 104 || store.quoteError?.error.errorCode === 106) {
      return InitialActionButtonState.IncreaseOrderSize as ActionButtonStateKeys;
    }
    return InitialActionButtonState.Error as ActionButtonStateKeys;
  }

  if (!store.quote) {
    return InitialActionButtonState.NoPricing as ActionButtonStateKeys;
  }

  if (store.isSending) {
    if (!store.receiver?.userInput) {
      return InitialActionButtonState.EnterAddress as ActionButtonStateKeys;
    }
    if (
      store.receiverError !== "" ||
      !isReceiverValid(store.receiver, store.address, store.ensName)
    ) {
      return InitialActionButtonState.SwapAndSendError as ActionButtonStateKeys;
    }
    if (store.receiver.isAddressLoading || store.quote.receiver !== store.receiver.address) {
      return InitialActionButtonState.AddressLoading as ActionButtonStateKeys;
    }
  }

  if (some(store.takerTokens, entry => entry.token === store.network.nativeCurrency.symbol)) {
    return InitialActionButtonState.WrappingQuote as ActionButtonStateKeys;
  }

  if (tokensToApprove.length > 0) {
    return InitialActionButtonState.InsufficientAllowance as ActionButtonStateKeys;
  }

  if (getQuoteWarnings(store.quote)) {
    return InitialActionButtonState.IgnoreAndReview as ActionButtonStateKeys;
  }

  if (store.isRequestingQuote) {
    return InitialActionButtonState.RequestingQuote as ActionButtonStateKeys;
  }

  return InitialActionButtonState.ReviewOrder as ActionButtonStateKeys;
};
const isQuantityLargerThanBalance = (store: IGlobalStore) => {
  return (
    store.takerTokens.filter(token => {
      const balanceForToken = store.balances.find(balance => balance.symbol === token.token)
        ?.balance.value;
      const inputtedQuantity = token.amount.value;
      return balanceForToken?.lt(inputtedQuantity);
    }).length > 0
  );
};

const INITIAL_BUTTON_TEXT_MAP: Record<ActionButtonStateKeys, string> = {
  WalletNotConnected: "Connect Wallet",
  InsufficientAllowance: "Continue",
  WrappingQuote: "Continue",
  Wrapping: "Wrap",
  Unwrapping: "Unwrap",
  InsufficientBalance: "Insufficient Balance",
  SelectTokens: "Select Tokens",
  WrongNetwork: "Wrong Network",
  NoPricing: "Enter Amounts",
  EnterAmounts: "Enter Amounts",
  ReviewOrder: "Review Order",
  IgnoreAndReview: "Ignore and Review Order",
  RequestingQuote: "Loading",
  Error: "Error",
  IncreaseOrderSize: "Increase Order Size",
  SwapAndSendError: "Check Wallet Address",
  EnterAddress: "Enter Recipient Address",
  AddressLoading: "Loading",
};

const BUTTON_DISABLED_MAP: Partial<Record<ActionButtonStateKeys, boolean>> = {
  WalletNotConnected: false,
  InsufficientAllowance: false,
  WrappingQuote: false,
  Wrapping: false,
  Unwrapping: false,
  InsufficientBalance: true,
  WrongNetwork: true,
  SelectTokens: true,
  NoPricing: true,
  ReviewOrder: false,
  IgnoreAndReview: false,
  RequestingQuote: true,
  EnterAmounts: true,
  Error: true,
  IncreaseOrderSize: true,
  SwapAndSendError: true,
  EnterAddress: true,
  AddressLoading: true,
};

const BUTTON_STYLES: Record<string, string> = {
  HappyState: "btn-bebop-primary text-white",
  ErrorState: "btn-grey",
  InbetweenState: "btn-white",
};

const BUTTON_STYLE_MAP: Record<ActionButtonStateKeys, string> = {
  WalletNotConnected: BUTTON_STYLES.HappyState,
  Wrapping: BUTTON_STYLES.HappyState,
  Unwrapping: BUTTON_STYLES.HappyState,
  WrappingQuote: BUTTON_STYLES.HappyState,
  InsufficientBalance: BUTTON_STYLES.ErrorState,
  InsufficientAllowance: BUTTON_STYLES.HappyState,
  WrongNetwork: BUTTON_STYLES.InbetweenState,
  SelectTokens: BUTTON_STYLES.ErrorState,
  NoPricing: BUTTON_STYLES.ErrorState,
  ReviewOrder: BUTTON_STYLES.HappyState,
  IgnoreAndReview: BUTTON_STYLES.HappyState,
  RequestingQuote: BUTTON_STYLES.ErrorState,
  EnterAmounts: BUTTON_STYLES.ErrorState,
  Error: BUTTON_STYLES.ErrorState,
  IncreaseOrderSize: BUTTON_STYLES.ErrorState,
  SwapAndSendError: BUTTON_STYLES.ErrorState,
  EnterAddress: BUTTON_STYLES.ErrorState,
  AddressLoading: BUTTON_STYLES.ErrorState,
};

const BUTTON_MODAL_MAP: Partial<Record<ActionButtonStateKeys, MODAL_SELECTORS>> = {
  WalletNotConnected: MODAL_SELECTORS.WALLET_CONNECT,
  WrappingQuote: MODAL_SELECTORS.WRAPPING_QUOTE,
  InsufficientAllowance: MODAL_SELECTORS.APPROVE_ALLOWANCE,
};

export const InitialActionButton: React.FC = () => {
  const [errorState, setErrorState] = useState<ActionButtonStateKeys | undefined>(undefined);
  const [buttonText, setButtonText] = useState<string>("");
  const isWrapPage = useLocation().pathname === BEBOP_ROUTES.Wrap;
  const state = useStoreState(GlobalStore);

  useEffect(() => {
    setErrorState(validateOrderEntry(state));
  }, [state]);

  const getClassName = useCallback((): string => {
    if (errorState) {
      return BUTTON_STYLE_MAP[errorState];
    }
    return BUTTON_STYLES.HappyState;
  }, [errorState]);

  useEffect(() => {
    if (!errorState) {
      // Push to Order Review
      setButtonText("Review Order");
    } else {
      setButtonText(INITIAL_BUTTON_TEXT_MAP[errorState]);
    }
  }, [errorState]);

  const handleOnClick = useCallback(async () => {
    if (
      errorState === InitialActionButtonState.ReviewOrder ||
      errorState === InitialActionButtonState.IgnoreAndReview ||
      errorState === InitialActionButtonState.InsufficientAllowance ||
      errorState === InitialActionButtonState.Wrapping ||
      errorState === InitialActionButtonState.Unwrapping
    ) {
      // Check if allowances need to be approved
      const isUSBased = await isUserUSBased();
      if (isUSBased) {
        GlobalStore.update(s => {
          s.modalSelector = MODAL_SELECTORS.REGION_RESTRICTED;
        });
        return;
      }
      const requiredSignatures = state.quote?.requiredSignatures ?? [];
      const tokensToApprove = getTokensToApprove(state.quote, state.allowances);
      if (
        (tokensToApprove.length > 0 && state.permitType === "permit2") ||
        requiredSignatures.length > 0
      ) {
        console.error(`Running approve store update`);
        const [revokeFirst, increase] = partition(tokensToApprove, entry =>
          shouldRevokeFirst(entry.symbol, state.allowances)
        );
        ApprovalStore.update(s => {
          s.requiredSignatures = requiredSignatures;
          s.tokensToApprove = tokensToApprove;
          s.initialAllowances = state.allowances;
          s.currentAllowances = state.allowances;
          s.tokensByFlow = {
            approve: increase,
            revoke: revokeFirst,
          };
          s.permitType = state.permitType;
          s.tokenMetadata = state.tokens ?? {};
        });
        GlobalStore.update(s => {
          s.modalSelector = MODAL_SELECTORS.APPROVE_ALLOWANCE;
        });
        return;
      } else {
        GlobalStore.update(s => {
          if (isWrapPage) {
            s.wrapStatus = WrapStatus.REVIEW;
          } else {
            if (
              errorState === InitialActionButtonState.Wrapping ||
              errorState === InitialActionButtonState.Unwrapping
            ) {
              s.wrapStatus = WrapStatus.REVIEW;
            }
            s.orderStatus = OrderStatus.REVIEW;
          }
        });
      }
    } else if (errorState) {
      GlobalStore.update(s => {
        s.modalSelector = BUTTON_MODAL_MAP[errorState];
      });
    }
  }, [errorState, isWrapPage, state.allowances, state.permitType, state.quote, state.tokens]);

  const getDisabledAtr = useCallback(() => {
    if (errorState) {
      return BUTTON_DISABLED_MAP[errorState];
    }
    return BUTTON_DISABLED_MAP.ReviewOrder;
  }, [errorState]);

  const getButtonText = useCallback(() => {
    if (state.isRequestingQuote) {
      return <Spinner visible={true} />;
    }
    return buttonText;
  }, [buttonText, state.isRequestingQuote]);

  const getButtonIcon = useCallback(() => {
    if (errorState === InitialActionButtonState.WalletNotConnected) {
      return (
        <WalletIcon
          css={css`
            margin-right: 4px;
          `}
        />
      );
    }
    return null;
  }, [errorState]);
  return (
    <div className={"action-button"}>
      <RegionRestrictedModal />
      {state.modalSelector === MODAL_SELECTORS.WRAPPING_QUOTE && (
        <WrappingQuoteModal modalSelector={state.modalSelector} ariaHideApp={true} />
      )}
      <ConnectButton.Custom>
        {({ account, chain, openConnectModal, mounted }) => {
          const ready = mounted;
          const connected = ready && account && chain;
          return (
            <div
              {...(!ready && {
                "aria-hidden": true,
                style: {
                  opacity: 0,
                  pointerEvents: "none",
                  userSelect: "none",
                },
              })}
            >
              <button
                type="button"
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
                className={`btn-block button-font-size ` + getClassName()}
                data-testid="action-btn"
                onClick={!connected ? openConnectModal : handleOnClick}
                disabled={getDisabledAtr()}
              >
                {getButtonIcon()}
                <span data-testid="action-btn-text">{getButtonText()}</span>
              </button>
            </div>
          );
        }}
      </ConnectButton.Custom>
    </div>
  );
};
